import { useQuery } from "@tanstack/react-query";
import SubscriptionRepository from "@/repositories/subscription.repo";
import { featureForModule, type ModuleKey } from "@/utils/modules";
import useAuth from "./auth.hook";

/**
 * Plan entitlements for the current business (from /subscriptions/me → plans.config features).
 * `hasFeature` returns true for super-admins and for features included in the active plan.
 */
const usePlanFeatures = () => {
  const { isSuperAdmin } = useAuth();
  const repository = new SubscriptionRepository();

  const { data, isSuccess } = useQuery({
    queryKey: ["plan-me"],
    queryFn: () => repository.me(),
    enabled: !isSuperAdmin,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const features = (data?.features ?? {}) as Record<string, boolean>;
  const hasFeature = (key?: string) => isSuperAdmin || !key || !!features[key];

  // Whether entitlements have actually arrived. Feature checks fail closed, so anything that acts
  // destructively on a negative (e.g. the route guard redirecting) must wait for this.
  const isPlanLoaded = isSuperAdmin || isSuccess;

  // Actual resource counts for this business — used to surface only the menus
  // the business has genuinely set up (e.g. hide hotel ops for a travel-only business).
  const usage = (data?.usage ?? {}) as Partial<Record<string, number>>;

  // Numeric entitlements. A limit of 0 means the vertical isn't part of the plan at all, which is
  // how the UI tells a hotel plan from a restaurant or travel one.
  const limits = (data?.limits ?? {}) as Partial<Record<string, number>>;

  // Super-admin per-business overrides: UI module keys hidden from this business.
  const hiddenModules = (data?.hidden_modules ?? []) as string[];
  // A module is visible unless it's in the hidden set. Super-admins always see everything.
  const isModuleVisible = (key?: string) => isSuperAdmin || !key || !hiddenModules.includes(key);

  // A module is reachable only if it is BOTH not hidden by an admin AND granted by the plan. Callers
  // that skip endpoints to avoid a 403 must use this — a plan gate 403s just as a hidden module does.
  const isModuleAllowed = (key?: string) =>
    isModuleVisible(key) && hasFeature(featureForModule(key as ModuleKey));

  // Expiry status of the business's paid subscription (for the dashboard warning banner).
  const subscription = data?.subscription;

  return {
    features,
    hasFeature,
    isPlanLoaded,
    usage,
    limits,
    hiddenModules,
    isModuleVisible,
    isModuleAllowed,
    subscription,
  };
};

export default usePlanFeatures;
